import axios from 'axios';

// Automatically detect the correct API URL
const getApiBaseUrl = () => {
  // If we have an environment variable, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production build, use relative path since we're serving from the same server
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // In development, use relative path since we have proxy setup
  // The proxy in package.json will forward /api requests to http://localhost:3000
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('pookieToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('pookieToken');
      localStorage.removeItem('pookieUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  getUsers: () => api.get('/auth/users'),
};

// Tasks API
export const tasksAPI = {
  getTasks: () => api.get('/tasks'),
  createTask: (taskData) => api.post('/tasks', taskData),
  updateTask: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  completeTask: (id) => api.patch(`/tasks/${id}/complete`),
  forceReset: () => api.post('/tasks/reset-recurring'),
};

// Store API
export const storeAPI = {
  getItems: () => api.get('/store'),
  getAdminItems: () => api.get('/store/admin'),
  createItem: (itemData) => api.post('/store', itemData),
  updateItem: (id, itemData) => api.put(`/store/${id}`, itemData),
  deleteItem: (id) => api.delete(`/store/${id}`),
  toggleItem: (id) => api.patch(`/store/${id}/toggle`),
};

// Inventory API
export const inventoryAPI = {
  getInventory: (status = 'all') => api.get(`/inventory?status=${status}`),
  purchaseItem: (purchaseData) => api.post('/inventory/purchase', purchaseData),
  useItem: (id, notes) => api.patch(`/inventory/${id}/use`, { notes }),
  deleteItem: (id) => api.delete(`/inventory/${id}`),
  getStats: () => api.get('/inventory/stats'),
};

// Purchases API
export const purchasesAPI = {
  getPurchases: () => api.get('/purchases'),
  getAdminPurchases: () => api.get('/purchases/admin'),
  purchaseItem: (purchaseData) => api.post('/purchases', purchaseData),
  fulfillPurchase: (id, notes) => api.patch(`/purchases/${id}/fulfill`, { notes }),
  cancelPurchase: (id) => api.patch(`/purchases/${id}/cancel`),
  getStats: () => api.get('/purchases/stats'),
};

export default api; 